import { Controller } from "@hotwired/stimulus";
import type { GridApi, RowDragEndEvent, RowDropZoneParams } from "ag-grid-enterprise";
import { showToast, type ToastType } from "../../../application/toolkit/toast_component/show_toast";

const BASE = "votes--dashboard--vote-boxes-panel-component";

interface VoteBoxElection {
  id: number;
  name: string;
}

interface ListRowOrganization {
  id: number;
  name: string;
}

interface ListRow {
  id: number;
  title: string;
  state: string;
  elections_count: number;
  organization: ListRowOrganization;
}

interface ListResponse {
  vote_boxes?: ListRow[];
  page?: number;
  per_page?: number;
  has_next?: boolean;
  create?: CreatableConfig;
}

interface CreatableOrg {
  id: number;
  name: string;
}

interface CreatableConfig {
  enabled: boolean;
  organizations: CreatableOrg[];
  inline_supported: boolean;
}

interface DetailRow {
  id: number;
  name: string;
}

interface DetailResponse {
  rows?: DetailRow[];
}

type ElectionsLoadState = "idle" | "loading" | "loaded" | "error";

interface BoxRowState {
  id: number;
  title: string;
  elections_count: number;
  organization: ListRowOrganization;
  expanded: boolean;
  loadState: ElectionsLoadState;
  elections: VoteBoxElection[];
}

export default class extends Controller {
  static targets = [
    "list",
    "empty",
    "headerActions",
    "emptyCta",
    "createForm",
    "listFooter",
    "listPrevButton",
    "listNextButton",
    "listPageLabel",
  ];
  static values = {
    voteBoxesUrl: String,
    newVoteBoxUrl: String,
    readOnly: { type: Boolean, default: false },
    allowCreate: { type: Boolean, default: true },
    linkOut: { type: Boolean, default: true },
    dashboardUrl: String,
    detailUrl: String,
  };

  declare readonly listTarget: HTMLElement;
  declare readonly emptyTarget: HTMLElement;
  declare readonly headerActionsTarget: HTMLElement;
  declare readonly hasHeaderActionsTarget: boolean;
  declare readonly emptyCtaTarget: HTMLElement;
  declare readonly hasEmptyCtaTarget: boolean;
  declare readonly createFormTarget: HTMLElement;
  declare readonly hasCreateFormTarget: boolean;
  declare readonly listFooterTarget: HTMLElement;
  declare readonly listPrevButtonTarget: HTMLButtonElement;
  declare readonly listNextButtonTarget: HTMLButtonElement;
  declare readonly listPageLabelTarget: HTMLElement;
  declare readonly voteBoxesUrlValue: string;
  declare readonly newVoteBoxUrlValue: string;
  declare readonly readOnlyValue: boolean;
  declare readonly allowCreateValue: boolean;
  declare readonly linkOutValue: boolean;
  declare readonly dashboardUrlValue: string;
  declare readonly detailUrlValue: string;

  private boxRows = new Map<number, BoxRowState>();
  private rowElements = new Map<number, HTMLElement>();
  private dropZoneParams = new Map<number, RowDropZoneParams>();
  private registeredBoxIds = new Set<number>();
  private gridApi: GridApi | null = null;
  private gridReadyHandler: ((event: Event) => void) | null = null;
  private voteBoxesChangedHandler: (() => void) | null = null;

  private listPage = 1;
  private listHasNext = false;
  private lastListRowCount = 0;

  private createConfig: CreatableConfig | null = null;
  private creating = false;

  connect() {
    if (!this.readOnlyValue) {
      this.gridReadyHandler = (event: Event) => {
        const detail = (event as CustomEvent<{ gridApi: GridApi | null }>).detail;
        this.gridApi = detail?.gridApi ?? null;
        this.registerPendingDropZones();
      };
      document.addEventListener("votes:dashboard:grid-ready", this.gridReadyHandler);

      document.dispatchEvent(new CustomEvent("votes:dashboard:request-grid-api"));
    }

    // Elections can also be filed into a box from the grid's bulk action, which knows nothing about
    // this panel's counts — it announces the change and the panel re-reads the page it is on.
    this.voteBoxesChangedHandler = () => {
      void this.loadListPage(this.listPage);
    };
    document.addEventListener("votes:dashboard:vote-boxes-changed", this.voteBoxesChangedHandler);

    void this.loadListPage(1);
  }

  disconnect() {
    if (this.gridReadyHandler) {
      document.removeEventListener("votes:dashboard:grid-ready", this.gridReadyHandler);
      this.gridReadyHandler = null;
    }
    if (this.voteBoxesChangedHandler) {
      document.removeEventListener("votes:dashboard:vote-boxes-changed", this.voteBoxesChangedHandler);
      this.voteBoxesChangedHandler = null;
    }
    this.teardownDropZones();
  }

  private async loadListPage(page: number) {
    try {
      const response = await fetch(`${this.voteBoxesUrlValue}?page=${page}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) return;
      const data = (await response.json()) as ListResponse;

      this.listPage = data.page ?? page;
      this.listHasNext = Boolean(data.has_next);
      if (data.create !== undefined) this.createConfig = data.create;

      this.renderListPage(data.vote_boxes ?? []);
      this.renderCreateAffordances();
    } catch {
    }
  }

  prevPage() {
    if (this.listPage <= 1) return;
    void this.loadListPage(this.listPage - 1);
  }

  nextPage() {
    if (!this.listHasNext) return;
    void this.loadListPage(this.listPage + 1);
  }

  private renderListPage(rows: ListRow[]) {
    this.lastListRowCount = rows.length;

    this.teardownDropZones();
    this.rowElements.clear();
    this.boxRows.clear();

    this.listTarget.innerHTML = "";
    rows.forEach((row) => {
      this.boxRows.set(row.id, {
        id: row.id,
        title: row.title,
        elections_count: row.elections_count,
        organization: row.organization,
        expanded: false,
        loadState: "idle",
        elections: [],
      });
      const el = this.buildRow(row.id);
      this.rowElements.set(row.id, el);
      this.listTarget.appendChild(el);
      this.registerDropZone(row.id);
    });

    this.updateListFooter();
    this.updateEmptyState();
  }

  private buildRow(boxId: number): HTMLElement {
    const box = this.boxRows.get(boxId);
    if (!box) throw new Error(`Unknown vote box id: ${boxId}`);

    const container = document.createElement("div");
    container.className = `${BASE}__row`;
    container.dataset.boxId = String(boxId);

    const main = document.createElement("div");
    main.className = `${BASE}__row-main`;

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = `${BASE}__row-toggle`;
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Toggle elections");
    toggle.addEventListener("click", () => this.toggleExpand(boxId));
    main.appendChild(toggle);

    const content = document.createElement("div");
    content.className = `${BASE}__row-content`;

    if (this.linkOutValue) {
      const link = document.createElement("a");
      link.className = `${BASE}__row-title-link`;
      link.href = this.dashboardUrlValue.replace("{id}", String(boxId));
      link.textContent = box.title;
      content.appendChild(link);
    } else {
      const title = document.createElement("span");
      title.className = `${BASE}__row-title`;
      title.textContent = box.title;
      content.appendChild(title);
    }

    const meta = document.createElement("span");
    meta.className = `${BASE}__row-meta`;
    meta.textContent = `${this.countLabel(box.elections_count)} · ${box.organization.name}`;
    content.appendChild(meta);

    main.appendChild(content);
    container.appendChild(main);

    const detail = document.createElement("div");
    detail.className = `${BASE}__row-detail`;
    detail.hidden = true;
    container.appendChild(detail);

    return container;
  }

  private updateListFooter() {
    this.listPrevButtonTarget.disabled = this.listPage <= 1;
    this.listNextButtonTarget.disabled = !this.listHasNext;
    this.listPageLabelTarget.textContent = `Page ${this.listPage}`;
  }

  private toggleExpand(boxId: number) {
    const box = this.boxRows.get(boxId);
    if (!box) return;

    box.expanded = !box.expanded;
    this.updateRowElement(boxId);

    if (box.expanded && box.loadState === "idle") {
      void this.loadBoxElections(boxId);
    }
  }

  private async loadBoxElections(boxId: number) {
    const box = this.boxRows.get(boxId);
    if (!box) return;

    box.loadState = "loading";
    this.updateRowElement(boxId);

    const url = this.detailUrlValue.replace("{vote_box_id}", String(boxId));

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      const data = (response.ok ? await response.json() : { rows: [] }) as DetailResponse;
      box.elections = (data.rows ?? []).map((row) => ({ id: row.id, name: row.name }));
      box.loadState = "loaded";
    } catch {
      box.loadState = "error";
      box.elections = [];
    }

    this.updateRowElement(boxId);
  }

  private updateRowElement(boxId: number) {
    const box = this.boxRows.get(boxId);
    const el = this.rowElements.get(boxId);
    if (!box || !el) return;

    const toggle = el.querySelector<HTMLElement>(`.${BASE}__row-toggle`);
    toggle?.setAttribute("aria-expanded", String(box.expanded));

    const meta = el.querySelector<HTMLElement>(`.${BASE}__row-meta`);
    if (meta) meta.textContent = `${this.countLabel(box.elections_count)} · ${box.organization.name}`;

    const detail = el.querySelector<HTMLElement>(`.${BASE}__row-detail`);
    if (!detail) return;
    detail.hidden = !box.expanded;
    this.renderRowDetail(detail, box);
  }

  private renderRowDetail(container: HTMLElement, box: BoxRowState) {
    container.innerHTML = "";

    if (!box.expanded) return;

    if (box.loadState === "loading") {
      const loading = document.createElement("p");
      loading.className = `${BASE}__election-loading`;
      loading.textContent = "Loading elections…";
      container.appendChild(loading);
      return;
    }

    if (box.elections.length === 0) {
      const empty = document.createElement("p");
      empty.className = `${BASE}__election-empty`;
      empty.textContent = "No elections in this box.";
      container.appendChild(empty);
      return;
    }

    box.elections.forEach((election) => {
      const row = document.createElement("div");
      row.className = `${BASE}__election-row`;

      const name = document.createElement("span");
      name.className = `${BASE}__election-name`;
      name.textContent = election.name;
      row.appendChild(name);

      if (!this.readOnlyValue) {
        const detachButton = document.createElement("button");
        detachButton.type = "button";
        detachButton.className = `${BASE}__detach-button`;
        detachButton.textContent = "Remove";
        detachButton.addEventListener("click", () => this.detach(box.id, election.id));
        row.appendChild(detachButton);
      }

      container.appendChild(row);
    });
  }

  private countLabel(count: number): string {
    return `${count} election${count === 1 ? "" : "s"}`;
  }

  private updateEmptyState() {
    const isEmpty = this.listPage === 1 && this.lastListRowCount === 0;

    this.emptyTarget.hidden = !isEmpty;
    if (this.hasEmptyCtaTarget) {
      this.emptyCtaTarget.hidden = !isEmpty || !this.createConfig?.enabled;
    }
  }

  private renderCreateAffordances() {
    if (this.readOnlyValue || !this.allowCreateValue || !this.hasHeaderActionsTarget) return;

    this.headerActionsTarget.innerHTML = "";

    if (!this.createConfig?.enabled) {
      this.emptyCtaTarget.innerHTML = "";
      this.updateEmptyState();
      return;
    }

    const newButton = document.createElement("button");
    newButton.type = "button";
    newButton.className = `${BASE}__new-button`;
    newButton.textContent = "+ New";
    newButton.addEventListener("click", () => this.handleNewClick());
    this.headerActionsTarget.appendChild(newButton);

    this.emptyCtaTarget.innerHTML = "";
    const ctaButton = document.createElement("button");
    ctaButton.type = "button";
    ctaButton.className = `${BASE}__empty-cta-button`;
    ctaButton.textContent = "+ Create a vote box";
    ctaButton.addEventListener("click", () => this.handleNewClick());
    this.emptyCtaTarget.appendChild(ctaButton);
    this.updateEmptyState();
  }

  private handleNewClick() {
    if (!this.createConfig) return;

    if (!this.createConfig.inline_supported) {
      window.location.href = this.newVoteBoxUrlValue;
      return;
    }

    this.showCreateForm();
  }

  private showCreateForm() {
    if (!this.createConfig) return;

    this.createFormTarget.innerHTML = "";
    this.createFormTarget.hidden = false;

    const form = document.createElement("form");
    form.className = `${BASE}__create-form`;

    const titleLabel = document.createElement("label");
    titleLabel.textContent = "Vote Box name";
    const titleInput = document.createElement("input");
    titleInput.type = "text";
    titleInput.className = `${BASE}__create-form-title`;
    titleInput.maxLength = 60;
    titleLabel.appendChild(titleInput);
    form.appendChild(titleLabel);

    const orgs = this.createConfig.organizations;
    let orgSelect: HTMLSelectElement | null = null;
    if (orgs.length > 1) {
      const orgLabel = document.createElement("label");
      orgLabel.textContent = "Organization";
      orgSelect = document.createElement("select");
      orgSelect.className = `${BASE}__create-form-org`;
      orgs.forEach((org) => {
        const option = document.createElement("option");
        option.value = String(org.id);
        option.textContent = org.name;
        orgSelect?.appendChild(option);
      });
      orgLabel.appendChild(orgSelect);
      form.appendChild(orgLabel);
    }

    const actions = document.createElement("div");
    actions.className = `${BASE}__create-form-actions`;

    const submitButton = document.createElement("button");
    submitButton.type = "submit";
    submitButton.className = `${BASE}__create-form-submit`;
    submitButton.textContent = "Create";
    actions.appendChild(submitButton);

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.className = `${BASE}__create-form-cancel`;
    cancelButton.textContent = "Cancel";
    cancelButton.addEventListener("click", () => this.hideCreateForm());
    actions.appendChild(cancelButton);

    form.appendChild(actions);

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const title = titleInput.value.trim();
      const organizationId = orgSelect ? Number(orgSelect.value) : orgs[0]?.id;
      if (!title || !organizationId) return;
      this.submitCreate(title, organizationId, submitButton);
    });

    this.createFormTarget.appendChild(form);
  }

  private hideCreateForm() {
    this.createFormTarget.hidden = true;
    this.createFormTarget.innerHTML = "";
  }

  private submitCreate(title: string, organizationId: number, submitButton: HTMLButtonElement) {
    if (this.creating) return;
    this.creating = true;
    submitButton.disabled = true;

    fetch(this.voteBoxesUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      credentials: "same-origin",
      body: JSON.stringify({ vote_box: { title, organization_id: organizationId } }),
    })
      .then(async (response) => {
        const data = (await response.json().catch(() => ({}))) as { errors?: string[] };
        if (!response.ok) {
          throw new Error(data.errors?.join(", ") || "Could not create vote box.");
        }
        this.hideCreateForm();
        void this.loadListPage(this.listPage);
      })
      .catch((error: Error) => {
        this.showToast(error.message || "Could not create vote box.", "danger");
      })
      .finally(() => {
        this.creating = false;
        submitButton.disabled = false;
      });
  }

  private registerPendingDropZones() {
    this.rowElements.forEach((_el, boxId) => this.registerDropZone(boxId));
  }

  private registerDropZone(boxId: number) {
    if (this.readOnlyValue || !this.gridApi || this.registeredBoxIds.has(boxId)) return;
    const container = this.rowElements.get(boxId);
    if (!container) return;

    const params: RowDropZoneParams = {
      getContainer: () => container,
      onDragEnter: () => container.classList.add(`${BASE}__row--drag-over`),
      onDragLeave: () => container.classList.remove(`${BASE}__row--drag-over`),
      onDragStop: (event: RowDragEndEvent) => this.handleDrop(boxId, event),
    };

    this.gridApi.addRowDropZone(params);
    this.dropZoneParams.set(boxId, params);
    this.registeredBoxIds.add(boxId);
  }

  private teardownDropZones() {
    if (this.gridApi) {
      this.dropZoneParams.forEach((params) => this.gridApi?.removeRowDropZone(params));
    }
    this.dropZoneParams.clear();
    this.registeredBoxIds.clear();
  }

  private handleDrop(boxId: number, event: RowDragEndEvent) {
    this.rowElements.get(boxId)?.classList.remove(`${BASE}__row--drag-over`);

    const box = this.boxRows.get(boxId);
    if (!box) return;

    const rows = (event.nodes ?? [])
      .map((node) => node.data as Record<string, unknown> | undefined)
      .filter((data): data is Record<string, unknown> => data?.id !== undefined && data?.id !== null);
    if (rows.length === 0) return;

    const electionIds = rows.map((row) => String(row.id));

    const alreadyPresent =
      box.loadState === "loaded" ? new Set(box.elections.map((election) => String(election.id))) : new Set<string>();
    const newElections = rows
      .filter((row) => !alreadyPresent.has(String(row.id)))
      .map((row) => ({ id: row.id as number, name: String(row.name ?? "") }));

    if (newElections.length === 0) return;

    const previousElections = box.elections;
    const previousCount = box.elections_count;
    if (box.loadState === "loaded") {
      box.elections = [...box.elections, ...newElections];
    }
    box.elections_count = box.elections_count + newElections.length;
    this.updateRowElement(boxId);

    fetch(`${this.voteBoxesUrlValue}/${boxId}/elections`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      credentials: "same-origin",
      body: JSON.stringify({ election_ids: electionIds }),
    })
      .then(async (response) => {
        if (response.ok) return;
        const data = (await response.json().catch(() => ({}))) as { errors?: string[] };
        throw new Error(data.errors?.join(", ") || "Could not attach election. It must be an active vote box.");
      })
      .catch((error: Error) => {
        box.elections = previousElections;
        box.elections_count = previousCount;
        this.updateRowElement(boxId);
        this.showToast(error.message || "Could not attach election.", "danger");
      });
  }

  private detach(boxId: number, electionId: number) {
    const box = this.boxRows.get(boxId);
    if (!box) return;

    const index = box.elections.findIndex((election) => String(election.id) === String(electionId));
    if (index === -1) return;

    const previousElections = box.elections;
    const previousCount = box.elections_count;
    box.elections = box.elections.filter((_, i) => i !== index);
    box.elections_count = Math.max(0, box.elections_count - 1);
    this.updateRowElement(boxId);

    fetch(`${this.voteBoxesUrlValue}/${boxId}/elections/${electionId}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      credentials: "same-origin",
    })
      .then(async (response) => {
        if (response.ok) return;
        const data = (await response.json().catch(() => ({}))) as { errors?: string[] };
        throw new Error(data.errors?.join(", ") || "Could not detach election.");
      })
      .catch((error: Error) => {
        box.elections = previousElections;
        box.elections_count = previousCount;
        this.updateRowElement(boxId);
        this.showToast(error.message || "Could not detach election.", "danger");
      });
  }

  private csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
  }

  private showToast(message: string, type: ToastType) {
    showToast(message, type);
  }
}
